import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../App.css';
import palefoSeal from '../assets/palefo.png';
import { useExchangeRate } from '../hooks/useExchangeRate';
import { useSettings } from '../hooks/useSettings';
import { useVoice } from '../hooks/useVoice';
import { setVolume, unlockAudio } from '../lib/audio';
import { translator } from '../lib/i18n';
import { FOREIGN, amountText, answerParts, cleanAmountInput, convert, formatAmount, rows } from '../lib/money';
import { namedCurrencies, parseMoney, switchRequest } from '../lib/parseMoney';
import { micProblem } from '../lib/recorder';
import { ASSISTANT_LANGUAGES, synthesize } from '../lib/voiceApi';
import { conversionSentence, moneyWords, switchedSentence } from '../shared/speech.mjs';
import AnswerBox from './AnswerBox';
import CurrencyRow from './CurrencyRow';
import ExchangeSheet from './ExchangeSheet';
import Hero from './Hero';
import RateBar from './RateBar';
import SettingsSheet from './SettingsSheet';
import VoiceDock from './VoiceDock';

// Phone-frame simulation (the Palefò family canon): at 480px and wider the app
// is a fixed 430x932 phone, scaled down to fit a shorter window.
const FRAME_W = 430, FRAME_H = 932, MIN_SCALE = 0.65;

function useFrameScale(frame) {
  useEffect(() => {
    const apply = () => {
      const el = frame.current;
      if (!el) return;
      const scale = window.innerWidth < 480 ? 1 : Math.max(MIN_SCALE,
        Math.min(1, (0.92 * window.innerWidth) / FRAME_W, (0.95 * window.innerHeight) / FRAME_H));
      el.style.transform = scale < 1 ? `scale(${scale})` : '';
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [frame]);
}

// The voice button beside the cog steps through these: loud, quiet, muted.
const VOLUMES = [1, 0.5, 0];
const VOLUME_ICON = {
  1: <><path d="M15.5 9a4 4 0 0 1 0 6" /><path d="M18 6.5a7.5 7.5 0 0 1 0 11" /></>,
  0.5: <path d="M15.5 9a4 4 0 0 1 0 6" />,
  0: <path d="M16 9.5l5 5M21 9.5l-5 5" />,
};

const MIC_NOTICE = { insecure: 'error.micInsecure', unsupported: 'error.micUnsupported' };
const ERROR_NOTICE = {
  mic_denied: 'error.micDenied',
  mic_missing: 'error.micMissing',
  mic_unsupported: 'error.micUnsupported',
  rate_limited: 'error.busy',
  voice_off: 'error.voiceOff',
};

export default function Gourdex() {
  const [settings, updateSettings] = useSettings();
  const voice = useVoice();
  const t = useMemo(() => translator(settings.lang), [settings.lang]);

  // The one amount the user gave, and the currency they gave it in. The other
  // rows are always derived from it — nothing else is stored.
  const [entry, setEntry] = useState({ currency: 'htg', text: '' });
  const [heard, setHeard] = useState(null);       // { transcript, target, bareDola, htd } from the last utterance
  const [reply, setReply] = useState(null);       // { text, audio }: the Assistant's answer to a question, shown in place of a conversion
  const [switched, setSwitched] = useState(null); // a currency code: the voice just changed the foreign currency to it
  const [notice, setNotice] = useState(null);     // an i18n key, shown in place of the answer
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exchangeOpen, setExchangeOpen] = useState(false);

  const frame = useRef(null);
  const dock = useRef(null);
  useFrameScale(frame);

  // The foreign row is the currency chosen on its pill (a setting) — unless an
  // utterance named another ("san ewo", "… an dola kanadyen"), which takes the
  // row for as long as that exchange is on screen.
  const foreignAsked = FOREIGN.find((c) => c !== settings.foreign && (entry.currency === c || heard?.target === c));
  const foreign = foreignAsked || settings.foreign;
  const exchange = useExchangeRate(foreign);
  // The rows on screen: gourdes and the foreign currency. The Haitian dollar's
  // row is a setting, off by default; while it is off, ask about it ("senk san
  // dola ayisyen", "… an dola ayisyen", "kisa dola ayisyen an ye?") and the
  // row is there for as long as that exchange is on screen, then goes again.
  const htdAsked = entry.currency === 'htd' || heard?.target === 'htd' || heard?.htd;
  const shown = rows(foreign).filter((c) => c !== 'htd' || settings.showHtd || htdAsked);
  // With no Haitian-dollar row on the page, a bare "dola" means the American one; the answer offers the other reading.
  const bareDola = settings.showHtd ? settings.bareDola : 'usd';

  // Muted, the voice is not merely silent: nothing is read out unasked, so
  // nothing is fetched to be read. Asking for it (Koute) turns the voice back on.
  const volume = VOLUMES.includes(settings.volume) ? settings.volume : 1;
  const audible = volume > 0;
  useEffect(() => { setVolume(volume); }, [volume]);
  const onVolume = () => {
    const next = VOLUMES[(VOLUMES.indexOf(volume) + 1) % VOLUMES.length];
    if (!next) voice.hush();
    updateSettings({ volume: next });
  };

  const { rate } = exchange;
  const amount = parseFloat(entry.text) || 0;
  const values = rate ? convert(amount, entry.currency, rate, foreign) : null;

  // The voice language is both what the mic listens for and what the answer
  // is spoken (and written) in. Kreyòl has its own voices; the rest share a set.
  const { voiceLang } = settings;
  const example = { example: moneyWords(500, foreign, voiceLang) };   // something to say, in that language
  const answer = (sum, currency, target) => answerParts(sum, currency, rate, target, shown);
  // What the box holds and the voice says: the change of currency just made, or the conversion.
  const spoken = switched ? { switched } : answer(amount, entry.currency, heard?.target);
  const sentence = spoken && (switched ? switchedSentence(switched, voiceLang) : conversionSentence(spoken.source, spoken.targets, voiceLang));

  const micIssue = micProblem();
  const voiceReady = voice.status.ready === true;
  const cast = voiceLang === 'ht' ? voice.status.voices : voice.status.speakers;
  const voiceChoice = (voiceLang === 'ht' ? settings.voiceId : settings.speaker) || cast[0]?.id;
  const speaker = cast.find((v) => v.id === voiceChoice)?.name;

  const say = async (parts) => {
    if (!parts || !voiceReady) return;
    try { await voice.speak(parts, { lang: voiceLang, voice: voiceChoice }); } catch (e) { setNotice(ERROR_NOTICE[e.message] || 'error.voice'); }
  };

  const onLevel = useCallback((level) => dock.current?.style.setProperty('--level', level.toFixed(3)), []);

  const onMic = async () => {
    unlockAudio();
    if (voice.phase === 'listening') return voice.finishListening();
    if (voice.phase !== 'idle') return voice.hush();
    setNotice(null);
    setHeard(null);
    let result;
    try { result = await voice.listen(voiceLang, onLevel); } catch (e) { return setNotice(ERROR_NOTICE[e.message] || 'error.voice'); }
    if (result.cancelled) return;
    if (result.noSpeech || !result.transcription) return setNotice('error.noSpeech');

    const transcript = result.transcription.trim();
    const parsed = parseMoney(transcript, { bareDola, lang: voiceLang });
    if (parsed.reason === 'too_large') {
      setHeard({ transcript });
      return setNotice('error.tooLarge');
    }
    // No amount in it: a request for another currency ("mete ewo") — or a question, and one for the Assistant.
    if (!parsed.ok) {
      const code = switchRequest(transcript, { bareDola, lang: voiceLang });
      if (!code) return question(transcript);
      choose(code);
      setSwitched(code);
      setHeard({ transcript });
      if (settings.autoSpeak && audible) say({ switched: code });
      return;
    }

    // No currency named: the amount goes to the row that was already active.
    const currency = parsed.currency || entry.currency;
    setReply(null);
    setSwitched(null);
    setEntry({ currency, text: amountText(parsed.amount) });
    setHeard({ transcript, target: parsed.target, bareDola: parsed.bareDola });
    if (!rate) return setNotice('error.noRate');
    if (settings.autoSpeak && audible) say(answer(parsed.amount, currency, parsed.target));
  };

  // A question about the exchange goes to the Palefò Assistant, with the rate on
  // screen so that its answer agrees with the page. It answers in Kreyòl, in its
  // own voice; the reply takes the answer box until the next amount.
  const question = async (transcript) => {
    setSwitched(null);
    setHeard({ transcript, htd: namedCurrencies(transcript, { lang: voiceLang }).includes('htd') });
    if (!voiceReady || !ASSISTANT_LANGUAGES.has(voiceLang)) return setNotice('error.noAmount');
    try {
      const answered = await voice.ask({
        question: transcript,
        lang: voiceLang,
        voice: settings.voiceId || voice.status.voices[0]?.id,
        rate,
        currency: foreign,
        source: exchange.origin || 'manual',
      });
      if (answered.cancelled) return;
      setReply(answered);
      if (settings.autoSpeak && audible && answered.audio) await voice.playClip(answered.audio);
    } catch (e) {
      setNotice(ERROR_NOTICE[e.message] || 'error.voice');
    }
  };

  // The BRH's reference rate, read out in Kreyòl from the Echanj screen.
  const hearRate = async (brhRate) => {
    unlockAudio();
    if (!audible) updateSettings({ volume: 1 });
    try { await voice.speakRate(brhRate, settings.voiceId || voice.status.voices[0]?.id); } catch (e) { setNotice(ERROR_NOTICE[e.message] || 'error.voice'); }
  };

  const playReply = async () => {
    try { await voice.playClip(reply.audio); } catch { setNotice('error.voice'); }
  };

  const onHear = () => {
    unlockAudio();
    if (voice.phase === 'speaking') return voice.hush();
    setNotice(null);
    if (!audible) updateSettings({ volume: 1 });   // asking to hear it is asking for the voice back
    if (reply) playReply(); else say(spoken);
  };

  // What is in the answer box, as text to paste or send: the Assistant's
  // reply, or the conversion's sentence with the figures under it.
  const shareText = reply ? reply.text : switched ? sentence : spoken && [
    sentence,
    [spoken.source, ...spoken.targets].map((m) => `${formatAmount(m.amount)} ${m.currency.toUpperCase()}`).join(' = '),
  ].join('\n');

  const onCopy = async () => {
    try { await navigator.clipboard.writeText(shareText); return true; } catch { return false; }
  };
  const onShare = () => navigator.share({ text: shareText }).catch(() => {});   // dismissing the sheet is not an error

  // The spoken answer as a file. The Assistant's reply came with its audio; a
  // conversion's is in memory if it has been played, and fetched (and billed) once if not.
  const onDownload = async () => {
    try {
      const bytes = reply ? reply.audio : await synthesize(spoken, { lang: voiceLang, voice: voiceChoice });
      const url = URL.createObjectURL(new Blob([bytes], { type: 'audio/mpeg' }));
      const link = Object.assign(document.createElement('a'), { href: url, download: 'kob-pa-m.mp3' });
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setNotice(ERROR_NOTICE[e.message] || 'error.voice');
    }
  };

  const onType = (currency, raw) => {
    setEntry({ currency, text: cleanAmountInput(raw) });
    setHeard(null);
    setReply(null);
    setSwitched(null);
    setNotice(null);
  };

  // The foreign currency chosen, on its pill or by voice: a setting, and the amount in that row goes with it.
  const choose = (c) => {
    updateSettings({ foreign: c });
    setEntry((e) => (e.currency === foreign ? { ...e, currency: c } : e));
    setReply(null);
    setNotice(null);
  };
  const onChoose = (c) => {
    choose(c);
    setHeard(null);
    setSwitched(null);
  };

  const onClear = () => {
    voice.hush();
    // Clearing ends the exchange, and with it a row that was only there because it was asked for.
    const stays = rows(settings.foreign).filter((c) => c !== 'htd' || settings.showHtd);
    setEntry((e) => ({ currency: stays.includes(e.currency) ? e.currency : 'htg', text: '' }));
    setHeard(null);
    setReply(null);
    setSwitched(null);
    setNotice(null);
  };

  // "Dola" was said bare: offer the other reading in one tap.
  const otherDola = entry.currency === 'usd' ? 'htd' : 'usd';
  const flipDola = () => {
    unlockAudio();
    setEntry((e) => ({ ...e, currency: otherDola }));
    if (settings.autoSpeak && audible) say(answer(amount, otherDola, heard?.target));
  };

  const busy = voice.phase === 'listening' || voice.phase === 'thinking';
  // The answer box holds the Assistant's reply when there is one, the conversion otherwise.
  const boxed = reply ? reply.text : sentence;
  const canPlay = voiceReady && (reply ? !!reply.audio : true);
  const hint = voice.status.ready === false ? t('error.voiceOff')
    : micIssue ? t(MIC_NOTICE[micIssue])
    : !voiceReady ? t('hint.type')
    : ASSISTANT_LANGUAGES.has(voiceLang) ? `${t('hint.speak', example)} ${t('hint.ask')}` : t('hint.speak', example);

  return (
    <div className="device-frame" ref={frame} data-phase={voice.phase}>
      <div className="card"><div className="card-glow" /></div>

      <div className="shell">
        <header className="topbar">
          <div className="brand-line">
            <h1 className="wordmark">Kòb pa m</h1>
            <button className="bar-btn volume" type="button" onClick={onVolume} data-level={volume}
              aria-label={t(`volume.${volume}`)} title={t(`volume.${volume}`)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3.5 9.5v5H7l4.5 4v-13L7 9.5H3.5z" />
                {VOLUME_ICON[volume]}
              </svg>
            </button>
            <button className="bar-btn gear" type="button" onClick={() => setSettingsOpen(true)}
              aria-label={t('settings.title')} title={t('settings.title')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>
          </div>
          <p className="tagline">{t('tagline')}</p>
        </header>

        <Hero />

        <main className="stage">
          <section className="exchange">
            <RateBar exchange={exchange} foreign={foreign} t={t} />
            {shown.map((c) => (
              <CurrencyRow
                key={c}
                code={c}
                name={t(`cur.${c}`)}
                active={c === entry.currency}
                value={c === entry.currency ? entry.text : amount > 0 && values ? formatAmount(values[c]) : ''}
                onType={(raw) => onType(c, raw)}
                choices={c === foreign ? FOREIGN.map((f) => [f, `${f.toUpperCase()} · ${t(`cur.${f}`)}`]) : undefined}
                onChoose={onChoose}
                chooseLabel={t('cur.choose')}
              />
            ))}
          </section>

          <div className="caption" aria-live="polite">
            {busy ? (
              <p className="caption-state">{t(`state.${voice.phase}`)}</p>
            ) : (
              <>
                {heard && (notice || !boxed) && (
                  <p className="caption-heard"><span>{t('heard')}</span> «{heard.transcript}»</p>
                )}
                {notice ? <p className="caption-notice">{t(notice, example)}</p>
                  : boxed ? (
                    <AnswerBox
                      text={boxed}
                      heard={heard?.transcript}
                      playing={voice.phase === 'speaking'}
                      canPlay={canPlay}
                      onPlay={onHear}
                      onStop={voice.hush}
                      onCopy={onCopy}
                      onShare={typeof navigator.share === 'function' ? onShare : undefined}
                      onDownload={canPlay ? onDownload : undefined}
                      speaker={reply ? t('box.assistant') : speaker}
                      // "Dola" was said bare: the other reading, one tap away, where the voice's name would be.
                      aside={!reply && heard?.bareDola && (
                        <button className="answer-chip" type="button" onClick={flipDola}>{t(`dola.${otherDola}`)}</button>
                      )}
                      t={t}
                    />
                  )
                  : amount > 0 && !rate ? <p className="caption-notice">{t('error.noRate')}</p>
                  : !heard && <p className="caption-hint">{hint}</p>}
              </>
            )}
          </div>

          <VoiceDock
            ref={dock}
            phase={voice.phase}
            canSpeak={voiceReady && !micIssue}
            canHear={canPlay && !!boxed && !busy}
            canClear={!!entry.text || !!heard || !!reply || !!switched}
            onExchange={() => setExchangeOpen(true)}
            onMic={onMic}
            onHear={onHear}
            onClear={onClear}
            t={t}
          />
        </main>

        <div className="seal"><img src={palefoSeal} alt="Palefò" /></div>
      </div>

      {exchangeOpen && (
        <ExchangeSheet
          lang={settings.lang}
          playing={voice.phase === 'speaking'}
          canHear={voiceReady}
          onHear={hearRate}
          onStop={voice.hush}
          onClose={() => setExchangeOpen(false)}
          t={t}
        />
      )}
      {settingsOpen && (
        <SettingsSheet
          settings={settings}
          update={updateSettings}
          voices={voice.status.voices}
          speakers={voice.status.speakers}
          onClose={() => setSettingsOpen(false)}
          t={t}
        />
      )}
    </div>
  );
}
